/**
 * WordPress content-model data layer (PRP-11).
 *
 * Fetchers + normalizers for the post types registered by the KGP content-model
 * engine (wordpress/mu-plugins/kgp-content-models.php): testimonials, team,
 * FAQs, gallery photos, events, and the site's grouping taxonomy.
 *
 * Contract with callers: every fetcher resolves to a (possibly empty) array.
 * - WordPress not configured (empty VITE_API_BASE_URL) → [] with no request.
 * - The engine isn't installed on the site (REST route 404) → [].
 * - Entries missing a required field are dropped, never rendered half-empty.
 * Other failures (network, 5xx) reject so React Query can retry; the
 * components treat that the same as "no data" and keep their coded fallback.
 */

import { getClient, wordpressConfigured } from './client';
import { groupingTaxonomy } from '../config/content';

/**
 * REST bases the engine's presets register by default. A site whose
 * content-models.json overrides a preset's `rest_base` must change it here too.
 */
export const WP_REST_BASES = {
  testimonial: 'testimonials',
  team: 'team',
  faq: 'faqs',
  gallery: 'gallery',
  event: 'events',
} as const;

// ============ Public shapes ============

export interface WpImage {
  src: string;
  srcSet: string;
  width: number;
  height: number;
  alt: string;
}

export interface Testimonial {
  id: number;
  quote: string;
  attribution: string;
  affiliation?: string;
}

export interface TeamMember {
  id: number;
  name: string;
  roleTitle?: string;
  bio?: string;
  photo: WpImage | null;
  order: number;
}

export interface Faq {
  id: number;
  question: string;
  /** Rendered WordPress HTML — display inside `.wp-content`. */
  answerHtml: string;
  order: number;
}

export interface GalleryItem {
  id: number;
  title: string;
  image: WpImage;
  caption?: string;
  credit: string;
}

export interface Term {
  id: number;
  slug: string;
  name: string;
}

export type EventStatus = 'scheduled' | 'cancelled' | 'postponed' | 'sold_out';

export interface EventTicket {
  href: string;
  label: string;
  /** true → absolute https URL (new tab); false → internal path (router link). */
  external: boolean;
}

/**
 * Public signup availability (PRP-12) — counts only, never personal data.
 * Absent when the site hasn't turned signups on for events, or the event has none.
 */
export interface EventSignups {
  enabled: boolean;
  /** The organizer's open/closed switch (closing time and event end are checked separately). */
  open: boolean;
  capacity: number;
  /** Never negative. */
  spotsLeft: number;
  /** ISO-8601, or null when signups stay open until the event. */
  closesAt: string | null;
}

export interface WpEvent {
  id: number;
  slug: string;
  title: string;
  /** Plain-text summary (the WP excerpt). */
  summary?: string;
  /** Rendered WordPress HTML — only present from getEventBySlug. */
  descriptionHtml?: string;
  /** ISO-8601 with UTC offset, from the engine's `event_times` field. */
  start: string;
  end: string | null;
  expires: string;
  allDay: boolean;
  /** The WordPress site's timezone (IANA name or ±HH:MM). */
  timezone: string;
  venueName?: string;
  venueAddress?: string;
  onlineUrl?: string;
  image: WpImage | null;
  ticket: EventTicket | null;
  priceText?: string;
  status: EventStatus;
  signups?: EventSignups;
}

// ============ Raw REST shapes (only the fields we request) ============

type Rendered = { rendered?: string };
type Acf = Record<string, unknown> | unknown[] | false | null | undefined;

interface RawPost {
  id: number;
  slug?: string;
  title?: Rendered;
  content?: Rendered;
  excerpt?: Rendered;
  menu_order?: number;
  acf?: Acf;
  event_times?: {
    start?: string | null;
    end?: string | null;
    expires?: string | null;
    all_day?: boolean;
    timezone?: string;
  } | null;
  /** PRP-12 engine field: null when signups are off. */
  signups?: unknown;
}

interface RawTerm {
  id: number;
  slug: string;
  name: string;
}

// ============ Normalizers (exported for tests) ============

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  hellip: '…', ndash: '–', mdash: '—',
  lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
};

/** Decodes the HTML entities WordPress emits in titles (&#8217; &amp; …). */
export function decodeEntities(input: string): string {
  return input.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code: string) => {
    if (code[0] === '#') {
      const n = code[1].toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isFinite(n) && n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : match;
    }
    return NAMED_ENTITIES[code.toLowerCase()] ?? match;
  });
}

/** Plain text from a WP string/HTML value: tags stripped, entities decoded, trimmed. */
export function toText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return decodeEntities(value.replace(/<[^>]*>/g, '')).replace(/\s+/g, ' ').trim();
}

const acfOf = (post: RawPost): Record<string, unknown> =>
  post.acf && typeof post.acf === 'object' && !Array.isArray(post.acf) ? post.acf : {};

/**
 * ACF image (return_format array, requested with acf_format=standard) → WpImage.
 * A number is a bare attachment ID — ACF returns that when the format isn't
 * applied or the caller can't read the attachment — so it counts as no image.
 */
export function normalizeImage(value: unknown): WpImage | null {
  if (!value || typeof value !== 'object') return null;
  const img = value as Record<string, unknown>;
  if (typeof img.url !== 'string' || img.url === '') return null;
  const width = typeof img.width === 'number' ? img.width : 0;
  const height = typeof img.height === 'number' ? img.height : 0;

  const candidates = new Map<number, string>(); // width → url (deduped)
  if (width > 0) candidates.set(width, img.url);
  const sizes = img.sizes && typeof img.sizes === 'object' ? (img.sizes as Record<string, unknown>) : {};
  for (const [name, url] of Object.entries(sizes)) {
    if (name.endsWith('-width') || name.endsWith('-height') || typeof url !== 'string') continue;
    const w = sizes[`${name}-width`];
    if (typeof w === 'number' && w > 0 && !candidates.has(w)) candidates.set(w, url);
  }
  const srcSet = [...candidates.entries()]
    .sort(([a], [b]) => a - b)
    .map(([w, url]) => `${url} ${w}w`)
    .join(', ');

  return { src: img.url, srcSet, width, height, alt: toText(img.alt) };
}

export function normalizeTestimonial(post: RawPost): Testimonial | null {
  const acf = acfOf(post);
  const quote = toText(acf.quote);
  const attribution = toText(acf.attribution);
  if (!quote || !attribution) return null;
  const affiliation = toText(acf.affiliation);
  return { id: post.id, quote, attribution, ...(affiliation && { affiliation }) };
}

export function normalizeTeamMember(post: RawPost): TeamMember | null {
  const acf = acfOf(post);
  const name = toText(post.title?.rendered);
  if (!name) return null;
  const roleTitle = toText(acf.role_title);
  const bio = toText(acf.bio);
  return {
    id: post.id,
    name,
    ...(roleTitle && { roleTitle }),
    ...(bio && { bio }),
    photo: normalizeImage(acf.photo),
    order: post.menu_order ?? 0,
  };
}

export function normalizeFaq(post: RawPost): Faq | null {
  const question = toText(post.title?.rendered);
  const answerHtml = post.content?.rendered?.trim() ?? '';
  if (!question || !answerHtml) return null;
  return { id: post.id, question, answerHtml, order: post.menu_order ?? 0 };
}

export function normalizeGalleryItem(post: RawPost): GalleryItem | null {
  const acf = acfOf(post);
  const image = normalizeImage(acf.image);
  const credit = toText(acf.credit);
  if (!image || !credit) return null;
  const caption = toText(acf.caption);
  return {
    id: post.id,
    title: toText(post.title?.rendered),
    image,
    ...(caption && { caption }),
    credit,
  };
}

const isIsoDate = (v: unknown): v is string => typeof v === 'string' && !Number.isNaN(Date.parse(v));
const EVENT_STATUSES: EventStatus[] = ['scheduled', 'cancelled', 'postponed', 'sold_out'];

/**
 * Ticket link from the ACF `ticket_link` text field: an absolute https URL
 * (external) or a site path starting with a single '/' (internal) — the same
 * rule the engine enforces on save. Anything else — http:, javascript:,
 * protocol-relative //host, bare words — is dropped.
 */
export function normalizeTicketLink(href: unknown, label: unknown): EventTicket | null {
  if (typeof href !== 'string') return null;
  const value = href.trim();
  const text = toText(label) || 'Get tickets';
  if (/^https:\/\/[^\s/]+[^\s]*$/i.test(value)) return { href: value, label: text, external: true };
  if (/^\/(?!\/)[^\s]*$/.test(value)) return { href: value, label: text, external: false };
  return null;
}

const count = (v: unknown): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
};

/** REST `signups` → EventSignups; missing, null, or not an object → undefined. */
export function normalizeSignups(raw: unknown): EventSignups | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const s = raw as Record<string, unknown>;
  return {
    enabled: s.enabled === true,
    open: s.open === true,
    capacity: count(s.capacity),
    spotsLeft: count(s.spots_left),
    closesAt: isIsoDate(s.closes_at) ? s.closes_at : null,
  };
}

export function normalizeEvent(post: RawPost): WpEvent | null {
  const acf = acfOf(post);
  const times = post.event_times ?? {};
  const title = toText(post.title?.rendered);
  const slug = typeof post.slug === 'string' ? post.slug : '';
  if (!title || !slug || !isIsoDate(times.start)) return null;
  const end = isIsoDate(times.end) ? times.end : null;
  const expires = isIsoDate(times.expires) ? times.expires : end ?? times.start;
  const status = EVENT_STATUSES.includes(acf.status as EventStatus) ? (acf.status as EventStatus) : 'scheduled';
  const onlineUrl = typeof acf.online_url === 'string' && /^https?:\/\//i.test(acf.online_url) ? acf.online_url : '';
  const summary = toText(post.excerpt?.rendered);
  const venueName = toText(acf.venue_name);
  const venueAddress = typeof acf.venue_address === 'string' ? acf.venue_address.replace(/<[^>]*>/g, '').trim() : '';
  const priceText = toText(acf.price_text);
  const descriptionHtml = post.content?.rendered?.trim();
  const signups = normalizeSignups(post.signups);
  return {
    id: post.id,
    slug,
    title,
    ...(summary && { summary }),
    ...(descriptionHtml && { descriptionHtml }),
    start: times.start,
    end,
    expires,
    allDay: Boolean(times.all_day ?? acf.all_day),
    timezone: typeof times.timezone === 'string' ? times.timezone : '',
    ...(venueName && { venueName }),
    ...(venueAddress && { venueAddress: decodeEntities(venueAddress) }),
    ...(onlineUrl && { onlineUrl }),
    image: normalizeImage(acf.image),
    ticket: normalizeTicketLink(acf.ticket_link, acf.ticket_label),
    ...(priceText && { priceText }),
    status,
    ...(signups && { signups }),
  };
}

const keep = <T,>(items: Array<T | null>): T[] => items.filter((x): x is T => x !== null);

// ============ Fetchers ============

const isNotFound = (error: unknown) =>
  typeof error === 'object' && error !== null && (error as { statusCode?: number }).statusCode === 404;

/** GET a WP REST collection; [] when WordPress is unconfigured or the route doesn't exist. */
async function getCollection<T>(path: string, params: Record<string, string | number>): Promise<T[]> {
  if (!(await wordpressConfigured())) return [];
  const client = await getClient();
  try {
    const response = await client.get<T[]>(path, { params });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    if (isNotFound(error)) return []; // engine not installed on this site
    throw error;
  }
}

const POST_FIELDS = 'id,title,acf,menu_order';

/** Resolves a grouping-taxonomy term slug to its id (null when the term doesn't exist). */
async function termIdForSlug(slug: string): Promise<number | null> {
  const [term] = await getCollection<RawTerm>(`/wp/v2/${groupingTaxonomy}`, { slug, _fields: 'id' });
  return term?.id ?? null;
}

/** Adds the grouping-taxonomy filter for `termSlug`; returns null when the term doesn't exist. */
async function termFilter(termSlug?: string): Promise<Record<string, number> | null> {
  if (!termSlug) return {};
  const id = await termIdForSlug(termSlug);
  return id === null ? null : { [groupingTaxonomy]: id };
}

export async function getTestimonials(limit = 3, termSlug?: string): Promise<Testimonial[]> {
  const filter = await termFilter(termSlug);
  if (filter === null) return [];
  const posts = await getCollection<RawPost>(`/wp/v2/${WP_REST_BASES.testimonial}`, {
    per_page: limit,
    acf_format: 'standard',
    _fields: POST_FIELDS,
    ...filter,
  });
  return keep(posts.map(normalizeTestimonial));
}

export async function getTeam(): Promise<TeamMember[]> {
  const posts = await getCollection<RawPost>(`/wp/v2/${WP_REST_BASES.team}`, {
    per_page: 100,
    orderby: 'menu_order',
    order: 'asc',
    acf_format: 'standard',
    _fields: POST_FIELDS,
  });
  return keep(posts.map(normalizeTeamMember));
}

export async function getFaqs(): Promise<Faq[]> {
  const posts = await getCollection<RawPost>(`/wp/v2/${WP_REST_BASES.faq}`, {
    per_page: 100,
    orderby: 'menu_order',
    order: 'asc',
    _fields: 'id,title,content,menu_order',
  });
  return keep(posts.map(normalizeFaq));
}

export async function getGallery(termSlug?: string, limit = 12): Promise<GalleryItem[]> {
  const filter = await termFilter(termSlug);
  if (filter === null) return [];
  const posts = await getCollection<RawPost>(`/wp/v2/${WP_REST_BASES.gallery}`, {
    per_page: limit,
    acf_format: 'standard',
    _fields: POST_FIELDS,
    ...filter,
  });
  return keep(posts.map(normalizeGalleryItem));
}

export async function getTerms(): Promise<Term[]> {
  const terms = await getCollection<RawTerm>(`/wp/v2/${groupingTaxonomy}`, {
    per_page: 100,
    hide_empty: 'false',
    _fields: 'id,slug,name',
  });
  return terms.map((t) => ({ id: t.id, slug: t.slug, name: toText(t.name) }));
}

const EVENT_LIST_FIELDS = 'id,slug,title,excerpt,acf,event_times,signups';

export interface GetEventsOptions {
  /** Only events that haven't expired, soonest first (default true). */
  upcoming?: boolean;
  /** Grouping-taxonomy term slug. */
  term?: string;
  limit?: number;
}

export async function getEvents({ upcoming = true, term, limit = 20 }: GetEventsOptions = {}): Promise<WpEvent[]> {
  const filter = await termFilter(term);
  if (filter === null) return [];
  const posts = await getCollection<RawPost>(`/wp/v2/${WP_REST_BASES.event}`, {
    per_page: limit,
    acf_format: 'standard',
    _fields: EVENT_LIST_FIELDS,
    ...(upcoming && { upcoming: 1 }),
    ...filter,
  });
  const events = keep(posts.map(normalizeEvent));
  if (!upcoming) return events;
  // The engine filters server-side; repeat it here so a page left open past an
  // event's expiry (or an older engine) never shows a finished event.
  const now = Date.now();
  return events
    .filter((e) => Date.parse(e.expires) >= now)
    .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
}

/** One event by slug, with its full description; null when it doesn't exist. */
export async function getEventBySlug(slug: string): Promise<WpEvent | null> {
  const [post] = await getCollection<RawPost>(`/wp/v2/${WP_REST_BASES.event}`, {
    slug,
    acf_format: 'standard',
    _fields: `${EVENT_LIST_FIELDS},content`,
  });
  return post ? normalizeEvent(post) : null;
}
